import { useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import type { DatumId } from "@nivo/pie";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { abbreviateNumber } from "@/react/helpers/number";
import { CHART_TYPES } from "@/react/hooks/analytics/chartTypes";
import { useLineChartComputed } from "@/react/hooks/analytics/useLineChartComputed";
import { parseDayIdToLocalDate } from "@/react/utils/dates/dayId";
import { useThemeContext } from "@circle-react/providers";
import { ChartContainer } from "../../shared/ChartContainer";
import { EmptyState } from "../../shared/EmptyState";
import { LegendComponent } from "../../shared/LegendComponent";
import { LoadingChartComponent } from "../../shared/LoadingChartComponent";
import { useContainerSize } from "../../shared/hooks/useContainerSize";
import { DATE_FORMAT } from "../constants";
import { buildRightAxis, buildYScale } from "./axisBuilders";
import { baseAxisProps, lineChartTheme } from "./constants";
import type { LineChartComponentProps } from "./types";

export const LineChartComponent = ({
  isLoading,
  className,
  title,
  data,
  tooltip,
  titleLabel,
  indexBy,
  seriesKeys,
  colors,
  curve = "monotoneX",
  enableArea = true,
  enableGridX = false,
  enableGridY = true,
  emptyStateTitle,
  emptyStateDescription,
  customTooltip,
  rootLocale,
  scaleType = "linear",
  showLegend = false,
  secondaryAxis,
  translateLegendLabels = true,
}: LineChartComponentProps) => {
  const { containerWidth, containerHeight, containerRef } = useContainerSize();
  const [activeId, setActiveId] = useState<DatumId | null>(null);

  const { isLightAppearance } = useThemeContext();
  const marginTop = 32;
  const marginBottom = 50;

  const {
    seriesColorById,
    gradientDefs,
    fillPattern,
    tickValues,
    formattedData,
    yTicks,
    yMinScale,
    yMaxScale,
    yMaxForNormalize,
    legendData,
    isSingleDataPoint,
    normalizedHeight,
  } = useLineChartComputed({
    data,
    seriesKeys,
    indexBy,
    colors,
    activeId,
    containerWidth,
    containerHeight,
    secondaryAxis,
    marginTop,
    marginBottom,
  });

  if (isLoading) {
    return <LoadingChartComponent type="line" className={className} />;
  }

  if (!data.length) {
    return (
      <EmptyState
        chartTitle={title}
        tooltip={tooltip}
        className={className}
        emptyTitle={emptyStateTitle}
        emptyDescription={emptyStateDescription}
      />
    );
  }

  const theme = lineChartTheme(Boolean(isLightAppearance));

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      titleLabel={titleLabel}
      className={className}
      data={data}
      chartType={CHART_TYPES.LINE}
    >
      <div className="flex flex-col gap-4">
        {showLegend && (
          <LegendComponent
            data={legendData}
            orientation="horizontal"
            setActiveId={setActiveId}
            className="self-start"
            rootLocale={translateLegendLabels ? rootLocale : undefined}
          />
        )}
        <div
          ref={containerRef}
          className="relative flex h-80 w-full overflow-visible"
          style={containerHeight ? { height: normalizedHeight } : undefined}
        >
          <ResponsiveLine
            data={formattedData}
            margin={{
              top: marginTop,
              right:
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                0
                  ? 80
                  : 50,
              bottom: marginBottom,
              left: 50,
            }}
            xScale={{ type: "point" }}
            yScale={buildYScale(
              Boolean(
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                  0,
              ),
              yMaxForNormalize,
              scaleType,
              yMinScale,
              yMaxScale,
            )}
            curve={curve}
            axisTop={null}
            axisRight={buildRightAxis(
              Boolean(
                (secondaryAxis &&
                  secondaryAxis.keys &&
                  secondaryAxis.keys.length) ||
                  0,
              ),
              yMaxForNormalize,
              secondaryAxis?.format,
              rootLocale,
            )}
            axisBottom={{
              ...baseAxisProps,
              legendOffset: 40,
              tickValues: tickValues,
              format: (value: string | number | Date) =>
                format(parseDayIdToLocalDate(String(value)), DATE_FORMAT),
            }}
            axisLeft={{
              ...baseAxisProps,
              legendOffset: -45,
              legendPosition: "middle",
              tickSize: 0,
              tickValues: yTicks,
              format: abbreviateNumber,
            }}
            enableGridX={enableGridX}
            enableGridY={enableGridY}
            gridYValues={yTicks}
            colors={seriesKeys.map(k => seriesColorById[k])}
            enablePoints={isSingleDataPoint}
            pointSize={8}
            pointLabelYOffset={-12}
            enableArea={enableArea}
            defs={gradientDefs}
            fill={fillPattern}
            useMesh
            onMouseEnter={datum => {
              if ("seriesId" in datum) {
                setActiveId(datum.seriesId as DatumId);
              }
            }}
            onMouseLeave={() => setActiveId(null)}
            tooltip={customTooltip}
            theme={theme}
            ariaLabel={t("settings.analytics.line_chart")}
          />
        </div>
      </div>
    </ChartContainer>
  );
};

LineChartComponent.displayName = "LineChartComponent";
